import React, { useEffect, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { LicenseIcon } from "../assets/icons/icons";
import { sortLicensesByState } from "../utils/licenses";
import useFocusTrap from "../hooks/useFocusTrap";

const isPdfFile = (file) => {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
};

export default function VerificationPage() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewMap, setReviewMap] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [removeDocument, setRemoveDocument] = useState(false);
  const [form, setForm] = useState({
    industry: "",
    state: "",
    category: "",
    licenseNumber: "",
    issuedOn: "",
    expiresOn: "",
    document: null,
    supportingDocument: null,
    notes: "",
  });
  const editModalRef = React.useRef(null);

  useFocusTrap({
    isOpen: showModal,
    containerRef: editModalRef,
    onClose: () => setShowModal(false),
    lockScroll: true,
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        setLicenses([]);
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from("licenses")
        .select("id,industry,category,state,status,issued_on,expires_on,supporting_document,description,license_number")
        .eq("user_id", userId)
        .eq("removed", false)
        .order("state", { ascending: true })
        .order("industry", { ascending: true })
        .order("category", { ascending: true });

      const rows = sortLicensesByState(data || []);
      setLicenses(rows);

      // Pull latest review/flag note per license so users know why it was rejected
      if (rows.length) {
        const ids = rows.map((r) => r.id);
        const { data: reviews } = await supabase
          .from("license_reviews")
          .select("license_id,decision,notes,created_at")
          .in("license_id", ids)
          .order("created_at", { ascending: false });
        const map = {};
        (reviews || []).forEach((r) => {
          if (!map[r.license_id]) {
            map[r.license_id] = r;
          }
        });
        setReviewMap(map);
      } else {
        setReviewMap({});
      }
      setLoading(false);

      // Reset modal state on refresh
      setShowModal(false);
      setEditingLicense(null);
      setRemoveDocument(false);
      setForm({
        industry: "",
        state: "",
        category: "",
        licenseNumber: "",
        issuedOn: "",
        expiresOn: "",
        document: null,
        supportingDocument: null,
        notes: "",
      });
    };
    load();
  }, []);

  const pending = licenses.filter((l) => (l.status || "").toLowerCase() === "pending");
  const issues = licenses.filter((l) => (l.status || "").toLowerCase() === "issues");
  const hasPending = pending.length > 0;
  const hasIssues = issues.length > 0;
  const hasInProcess = hasPending || hasIssues;

  const openEditModal = (lic) => {
    setEditingLicense(lic);
    setForm({
      industry: lic.industry || "",
      state: lic.state || "",
      category: lic.category || "",
      licenseNumber: lic.license_number || lic.licenseNumber || "",
      issuedOn: lic.issued_on || "",
      expiresOn: lic.expires_on || "",
      document: null,
      supportingDocument: lic.supporting_document || null,
      notes: lic.description || "",
    });
    setRemoveDocument(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingLicense) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;

    if (!form.document && (!form.supportingDocument || removeDocument)) {
      alert("Please upload a PDF of your license.");
      return;
    }
    if (form.document && !isPdfFile(form.document)) {
      alert("Please upload a PDF file.");
      return;
    }

    let supportingDocPath = removeDocument ? null : form.supportingDocument || null;

    if (form.document) {
      const ext = form.document.name.split(".").pop();
      const filePath = `${userId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("license_docs")
        .upload(filePath, form.document, { upsert: true });
      if (uploadError) {
        alert("Error uploading document. Please try again.");
      } else {
        supportingDocPath = filePath;
        setRemoveDocument(false);
      }
    }

    const payload = {
      industry: form.industry,
      state: form.state,
      category: form.category,
      license_number: form.licenseNumber,
      issued_on: form.issuedOn || null,
      expires_on: form.expiresOn || null,
      supporting_document: supportingDocPath || null,
      description: form.notes,
      status: "pending",
      verified: false,
    };

    const { error } = await supabase
      .from("licenses")
      .update(payload)
      .eq("id", editingLicense.id)
      .eq("user_id", userId);
    if (error) {
      alert("Error saving license. Please try again.");
    } else {
      // Refresh list
      const { data } = await supabase
        .from("licenses")
        .select("id,industry,category,state,status,issued_on,expires_on,supporting_document,description,license_number")
        .eq("user_id", userId)
        .eq("removed", false)
        .order("state", { ascending: true })
        .order("industry", { ascending: true })
        .order("category", { ascending: true });
      setLicenses(sortLicensesByState(data || []));
      setShowModal(false);
      setEditingLicense(null);
      setRemoveDocument(false);
    }
  };

  const steps = [
    {
      title: "Submission received",
      desc: "Your documents were uploaded successfully.",
      state: hasInProcess ? "done" : "muted",
    },
    {
      title: "Under review",
      desc: "We'll carefully verify your license details and documents while under the pending status.",
      state: hasPending ? "active blink" : hasIssues ? "done" : "muted",
    },
    {
      title: "Verification decision",
      desc: "Approved licenses appear on your Licenses page; flagged items will show in Issues.",
      state: hasIssues ? "active" : "muted",
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Track your verification requests</p>
          <h1 data-page-heading="true">Verification</h1>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>Verification Process</h3>
        </div>
        <div className="timeline">
          {steps.map((step, idx) => (
            <div className="timeline-item" key={step.title}>
              <div className={`timeline-dot ${step.state}`} />
              {idx < steps.length - 1 && <div className={`timeline-line ${step.state}`} />}
              <div className="timeline-content">
                <p className="timeline-step">STEP {idx + 1}</p>
                <p className="timeline-title">{step.title}</p>
                <p className="muted">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3>In Process</h3>
          <div className="badges" style={{ gap: 6 }}>
            <span className="badge amber">Pending {pending.length}</span>
            <span className="badge red">Issues {issues.length}</span>
          </div>
        </div>
        {loading && <p className="muted">Loading...</p>}
        {!loading && pending.length === 0 && issues.length === 0 && (
          <p className="muted">No licenses currently in review. Approved licenses are in the Licenses page.</p>
        )}
        <div className="license-list" style={{ gap: 10 }}>
          {licenses
            .filter((lic) => ["pending", "issues"].includes((lic.status || "").toLowerCase()))
            .map((lic) => (
              <div key={lic.id} className="license-card" style={{ padding: 14 }}>
                <div className="license-top" style={{ alignItems: "center" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <span className="card-line-icon">
                        <LicenseIcon className="card-line-icon" />
                      </span>
                      <p className="pill">{lic.industry || "License"}</p>
                    </div>
                    <h4>{lic.category || "Category"}</h4>
                    <p className="muted">State: {lic.state || "-"}</p>
                  </div>
                  <span className={`badge ${lic.status === "issues" ? "red" : "amber"}`}>
                    {lic.status || "pending"}
                  </span>
                </div>
                <div className="license-meta" style={{ marginTop: 4 }}>
                  <span>Issued: {lic.issued_on || "-"}</span>
                  <span>Expires: {lic.expires_on || "-"}</span>
                </div>
                {(lic.status || "").toLowerCase() === "issues" && (
                  <div style={{ marginTop: 10 }}>
                    <p className="muted" style={{ marginBottom: 6 }}>
                      Reason: {reviewMap[lic.id]?.notes || "Please update and resubmit."}
                    </p>
                    <button
                      className="primary"
                      type="button"
                      onClick={() => openEditModal(lic)}
                    >
                      Edit &amp; resubmit
                    </button>
                  </div>
                )}
              </div>
          ))}
        </div>
      </div>

      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div
            ref={editModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="verification-edit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="verification-edit-modal-title">Edit License</h3>
              <button className="icon-btn" type="button" aria-label="Close edit license dialog" onClick={() => { setShowModal(false); setEditingLicense(null); }}>x</button>
            </div>
            <form className="form-grid" onSubmit={handleSubmit}>
              <div className="form-row">
                <label htmlFor="verification-industry">Industry *</label>
                <input
                  id="verification-industry"
                  required
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label htmlFor="verification-state">State *</label>
                <input
                  id="verification-state"
                  required
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label htmlFor="verification-category">Category</label>
                <input
                  id="verification-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label htmlFor="verification-license-number">License Number *</label>
                <input
                  id="verification-license-number"
                  required
                  value={form.licenseNumber}
                  onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })}
                />
              </div>
              <div className="form-row dual">
                <div>
                  <label htmlFor="verification-issued-on">Issued On</label>
                  <input
                    id="verification-issued-on"
                    type="date"
                    value={form.issuedOn || ""}
                    onChange={(e) => setForm({ ...form, issuedOn: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="verification-expires-on">Expires On</label>
                  <input
                    id="verification-expires-on"
                    type="date"
                    value={form.expiresOn || ""}
                    onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <label htmlFor="verification-supporting-document">Supporting Document (PDF) *</label>
                <input
                  id="verification-supporting-document"
                  type="file"
                  accept="application/pdf"
                  required={!form.supportingDocument || removeDocument}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    if (file && !isPdfFile(file)) {
                      alert("Please upload a PDF file.");
                      e.target.value = "";
                      setForm({ ...form, document: null });
                      return;
                    }
                    setForm({ ...form, document: file });
                  }}
                />
                {form.supportingDocument && !removeDocument && (
                  <p className="muted" style={{ marginTop: 6 }}>
                    Current file: {form.supportingDocument}{" "}
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => { setRemoveDocument(true); setForm({ ...form, supportingDocument: null }); }}
                    >
                      Remove
                    </button>
                  </p>
                )}
              </div>
              <div className="form-row">
                <label htmlFor="verification-notes">Notes</label>
                <textarea
                  id="verification-notes"
                  rows="3"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Add details for the reviewer"
                />
              </div>
              <div className="button-row" style={{ justifyContent: "flex-end" }}>
                <button className="ghost" type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="primary" type="submit">Save License</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
